import { useState } from 'react';

import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { DeleteConfirmationDialog } from '@/components/shared/DeleteConfirmationDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { useDeleteMetricDefinition, useMetricDefinitionList } from '@/hooks/useMetricDefinitions';
import { usePageTitle } from '@/hooks/usePageTitle';

export const MetricDefinitionsListPage = () => {
  usePageTitle('Metric Definitions');
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: metrics, isLoading } = useMetricDefinitionList();
  const deleteMutation = useDeleteMetricDefinition();
  const [deleteTarget, setDeleteTarget] = useState<{ key: string } | null>(null);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.key);
      toast({
        variant: 'success',
        title: 'Deleted',
        description: `Metric '${deleteTarget.key}' removed.`,
      });
      setDeleteTarget(null);
    } catch {
      toast({ variant: 'destructive', title: 'Error', description: 'Failed to delete metric.' });
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
      </div>
    );
  }

  const codeMetrics = (metrics ?? []).filter((m) => m.source === 'code');
  const dbMetrics = (metrics ?? []).filter((m) => m.source === 'db');

  return (
    <div className="space-y-6 p-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <div>
            <CardTitle>Metric Definitions</CardTitle>
            <CardDescription>
              Metrics referenced by dashboards. Code-defined entries are read-only; DB entries can
              be edited from here.
            </CardDescription>
          </div>
          <Button onClick={() => navigate('/metric-definitions/new')}>
            <Plus className="mr-2 h-4 w-4" /> New metric
          </Button>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Key</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Tags</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {[...dbMetrics, ...codeMetrics].map((m) => (
                <TableRow key={m.key}>
                  <TableCell className="font-mono text-xs">{m.key}</TableCell>
                  <TableCell className="max-w-md truncate">{m.description}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {m.tags.map((t) => (
                        <Badge key={t} variant="outline" className="text-[10px]">
                          {t}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={m.source === 'code' ? 'secondary' : 'default'}>
                      {m.source}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={m.enabled ? 'default' : 'outline'}>
                      {m.enabled ? 'enabled' : 'disabled'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {m.source === 'db' ? (
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            navigate(`/metric-definitions/${encodeURIComponent(m.key)}`)
                          }
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setDeleteTarget({ key: m.key })}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-xs">read-only</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <DeleteConfirmationDialog
        open={!!deleteTarget}
        resourceName="metric definition"
        resourceIdentifier={deleteTarget?.key}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
};
